"use client";

import { useEffect, useRef } from "react";
import type { CSSProperties, ReactElement } from "react";
import {
  HEATMAP_MONTHS,
  type HeatmapCell,
  type HeatmapTwinkle,
  type HeatmapWeek,
  SHIP_HEATMAP,
} from "./developers-ship-heatmap-data";
import "./developers-ship-heatmap.css";
import { useTranslations } from "next-intl";

const DAY_LABELS: readonly {
  readonly id: string;
  readonly label: string | null;
}[] = [
  { id: "sun", label: null },
  { id: "mon", label: "Mon" },
  { id: "tue", label: null },
  { id: "wed", label: "Wed" },
  { id: "thu", label: null },
  { id: "fri", label: "Fri" },
  { id: "sat", label: null },
];

const LEGEND_COLORS: readonly string[] = [
  "rgba(255,255,255,0.07)",
  "#1D473A",
  "#2F7058",
  "#47A382",
  "#6FE3B4",
];

/** Native pixel width of the heatmap card (padding + labels + 52 weeks). */
const HEATMAP_W: number = 915;

function twinkleClass(kind: HeatmapTwinkle): string {
  if (kind === "fore") return "dvf-hm-cell dvf-hm-twF";
  if (kind === "back") return "dvf-hm-cell dvf-hm-twB";
  return "dvf-hm-cell";
}

function cellStyle(cell: HeatmapCell): CSSProperties {
  if (cell.twinkle === "none") return { background: cell.color };
  return {
    background: cell.color,
    animationDuration: cell.duration,
    animationDelay: cell.delay,
  };
}

function HeatmapCard(): ReactElement {
  const t = useTranslations("public.developersShipHeatmap");
  const boxRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box: HTMLDivElement | null = boxRef.current;
    if (!box) return;

    function applyScale(): void {
      const el: HTMLDivElement | null = boxRef.current;
      const card: HTMLDivElement | null = cardRef.current;
      if (!el || !card) return;
      if (window.matchMedia("(min-width: 768px)").matches) {
        el.style.removeProperty("height");
        el.style.setProperty("--dvf-hm-scale", "1");
        return;
      }
      const scale: number = el.clientWidth / HEATMAP_W;
      el.style.setProperty("--dvf-hm-scale", String(scale));
      el.style.height = `${card.offsetHeight * scale}px`;
    }

    applyScale();
    const observer: ResizeObserver = new ResizeObserver(applyScale);
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="dvf-hm-scale" ref={boxRef}>
      <div className="dvf-hm-card" ref={cardRef}>
        <div className="dvf-hm-head">
          <span className="dvf-hm-count">
            {SHIP_HEATMAP.contribCount} {t("contributionsTheLastYear")}
          </span>
          <span className="dvf-hm-meta">{t("last12Months")}</span>
        </div>

        <div className="dvf-hm-body">
          <div className="dvf-hm-days">
            {DAY_LABELS.map((day: (typeof DAY_LABELS)[number]) => (
              <span className="dvf-hm-day" key={day.id}>
                {day.label ?? ""}
              </span>
            ))}
          </div>
          <div>
            <div className="dvf-hm-months">
              {HEATMAP_MONTHS.map((month: string) => (
                <span className="dvf-hm-month" key={month}>
                  {month}
                </span>
              ))}
            </div>
            <div className="dvf-hm-grid">
              {SHIP_HEATMAP.weeks.map((week: HeatmapWeek) => (
                <div className="dvf-hm-week" key={week.id}>
                  {week.cells.map((cell: HeatmapCell) => (
                    <span
                      className={twinkleClass(cell.twinkle)}
                      key={cell.id}
                      style={cellStyle(cell)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dvf-hm-foot">
          <span className="dvf-hm-note">{t("weCountMergesNot")}</span>
          <span className="dvf-hm-legend">
            {t("less")}
            {LEGEND_COLORS.map((color: string) => (
              <span
                className="dvf-hm-swatch"
                key={color}
                style={{ background: color }}
              />
            ))}
            {t("more")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Dark GitHub-style year grid that spells SHIP — shipping culture band. */
export function DevelopersShipHeatmap({
  after,
}: {
  readonly after?: ReactElement;
}): ReactElement {
  const t = useTranslations("public.developersShipHeatmap");
  return (
    <section id="ship" className="dvf-section">
      <div className="dvf-section-inner">
        <div className="dvf-hm-box">
          <div className="dvf-hm-media">
            <HeatmapCard />
          </div>
          <div className="dvf-hm-copy">
            <h2 className="dvf-section-title">{t("weOptimizeForShipping")}</h2>
            <p className="dvf-section-intro">{t("theProductGetsBetter")}</p>
          </div>
          {after !== undefined ? (
            <div className="dvf-hm-after">{after}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
